import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

import { useNavigate } from 'react-router-dom';
import ProductAutocomplete from '../../components/common/ProductAutocomplete';
import { useSocket } from '../../context/SocketContext';


const ProductImage = ({ src, alt, name, className = "w-full h-full object-cover" }) => {
    const [error, setError] = useState(false);

    useEffect(() => {
        setError(false);
    }, [src]);

    const getBaseUrl = () => {
        if (window.location.port === '3000') {
            return `${window.location.protocol}//${window.location.hostname}:3001`;
        }
        return '/api';
    };

    const finalSrc = (src && src.startsWith('/') && !src.startsWith('//'))
        ? `${getBaseUrl()}${src}`
        : src;

    const handleError = () => {
        setError(true);
    };

    if (!src || error) {
        return (
            <div className={`${className} bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400`}>
                <span className="material-symbols-outlined text-[20px]">image_not_supported</span>
            </div>
        );
    }

    return (
        <img
            src={finalSrc}
            alt={alt || name}
            className={className}
            onError={handleError}
        />
    );
};


const StockScanner = () => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const { socket } = useSocket();

    const navigate = useNavigate();
    const [warehouses, setWarehouses] = useState([]);
    const [recentMovements, setRecentMovements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        sku: '',
        warehouseId: '',
        locationId: '',
        quantity: 1,
        unit: '',
        description: '',
        type: 'Entrée'
    });
    const [locations, setLocations] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // New states for alignment with mobile
    const [offProduct, setOffProduct] = useState(null);
    const [unknownProduct, setUnknownProduct] = useState(false);
    const [manualForm, setManualForm] = useState({ name: '', description: '' });
    const [capturedImage, setCapturedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);




    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const [wRes, mRes] = await Promise.all([
                fetch(`/api/warehouses?companyId=${user.companyId}`),
                fetch(`/api/stock-movements?companyId=${user.companyId}&limit=5`)
            ]);

            if (wRes.ok) {
                const wData = await wRes.json();
                setWarehouses(wData);
                if (wData.length > 0 && !formData.warehouseId) {
                    setFormData(prev => ({ ...prev, warehouseId: String(wData[0].id) }));
                }
            }
            if (mRes.ok) {
                const mData = await mRes.json();
                if (mData && typeof mData === 'object' && !Array.isArray(mData)) {
                    setRecentMovements(mData.movements || []);
                } else if (Array.isArray(mData)) {
                    setRecentMovements(mData);
                } else {
                    setRecentMovements([]);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [user?.companyId, formData.warehouseId]);

    useEffect(() => {
        const fetchLocations = async () => {
            if (!formData.warehouseId || !user?.companyId) {
                setLocations([]);
                return;
            }
            try {
                const res = await fetch(`/api/locations/with-path?warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
                if (res.ok) {
                    const all = await res.json();
                    setLocations(all.filter(l => l.type === 'Slot'));
                }
            } catch (e) {
                console.error("Error fetching locations:", e);
            }
        };
        fetchLocations();
    }, [formData.warehouseId, user?.companyId]);



    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const searchProduct = async (sku) => {
        if (!sku || !formData.warehouseId) return;
        setLoading(true);
        setSelectedProduct(null);
        setOffProduct(null);
        setUnknownProduct(false);
        setManualForm({ name: '', description: '' });
        setCapturedImage(null);
        setImagePreview(null);

        try {
            const prodRes = await fetch(`/api/products/search?sku=${sku}&warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
            const prodData = await prodRes.json();

            if (prodData && prodData.length > 0) {
                const product = prodData[0];
                setSelectedProduct(product);
                setFormData(prev => ({
                    ...prev,
                    sku: product.sku,
                    locationId: product.locations && product.locations.length > 0 ? String(product.locations[0].id) : ''
                }));
                showToast(`Produit trouvé : ${product.name}`, "success");
            } else {
                // Not found locally, ask for external search
                showConfirm({
                    title: 'Produit non trouvé',
                    message: `Souhaitez-vous chercher "${sku}" sur les bases de données externes ?`,
                    confirmText: 'Chercher en ligne',
                    cancelText: 'Saisie manuelle',
                    type: 'info',
                    onConfirm: async () => {
                        setLoading(true);
                        try {
                            const extRes = await fetch(`/api/products/proxy-external/${sku}?companyId=${user.companyId}`);
                            const extData = await extRes.json();
                            if (extRes.ok && extData.found) {
                                setOffProduct({ ...extData, sku });
                                showToast(`Trouvé sur ${extData.source}`, "info");
                            } else {
                                setUnknownProduct(true);
                                showToast("Introuvable en ligne", "warning");
                            }
                        } catch (e) {
                            showToast("Erreur recherche externe", "error");
                        } finally {
                            setLoading(false);
                        }
                    },
                    onCancel: () => {
                        setUnknownProduct(true);
                    }
                });
            }
        } catch (err) {
            console.error(err);
            showToast("Erreur lors de la recherche", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCapturedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleCreateProduct = async (status, baseProduct = null) => {
        setLoading(true);
        try {
            let imageUrl = baseProduct?.imageUrl || null;

            // Upload image if selected
            if (capturedImage) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', capturedImage);
                const upRes = await fetch('/api/upload/product-image', {
                    method: 'POST',
                    body: uploadFormData
                });
                if (upRes.ok) {
                    const upData = await upRes.json();
                    imageUrl = upData.imageUrl;
                }
            }

            const productData = {
                name: baseProduct ? baseProduct.name : manualForm.name,
                description: baseProduct ? (baseProduct.description || '') : manualForm.description,
                sku: baseProduct ? baseProduct.sku : formData.sku,
                barcode: baseProduct ? baseProduct.sku : formData.sku,
                companyId: user.companyId,
                status: status, // 'Actif' or 'Brouillon'
                categoryId: null,
                imageUrl: imageUrl,
                initialQuantity: 0,
                warehouseId: formData.warehouseId
            };

            const response = await fetch('/api/products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(productData)
            });

            if (response.ok) {
                const newProduct = await response.json();
                showToast(`Produit créé (${status})`, "success");
                setSelectedProduct(newProduct);
                setOffProduct(null);
                setUnknownProduct(false);
                setFormData(prev => ({ ...prev, sku: newProduct.sku }));
                fetchData();
            } else {
                const err = await response.json();
                showToast(err.error || "Erreur lors de la création", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur serveur lors de la création", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!socket) return;

        const handleUpdate = () => {
            fetchData();
        };

        socket.on('stock_updated', handleUpdate);

        return () => {
            socket.off('stock_updated', handleUpdate);
        };
    }, [socket, fetchData]);

    const handleMovement = async (type) => {
        if (!formData.sku || !formData.warehouseId || formData.quantity <= 0 || !formData.unit) {
            showToast("Veuillez remplir tous les champs obligatoires (dont l'unité)", "warning");
            return;
        }

        setLoading(true);
        try {
            let product = selectedProduct;

            // If no product selected, try one last check with SKU
            if (!product) {
                const prodRes = await fetch(`/api/products/search?sku=${formData.sku}&warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
                const prodData = await prodRes.json();
                if (prodData && prodData.length > 0) {
                    product = prodData[0];
                }
            }

            if (!product) {
                showToast("Produit non trouvé dans cet entrepôt", "error");
                setLoading(false);
                return;
            }

            const response = await fetch('/api/stock-movements', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    warehouseId: formData.warehouseId,
                    userId: user.id,
                    companyId: user.companyId,
                    type: type,
                    quantity: formData.quantity,
                    unit: formData.unit,
                    locationId: formData.locationId || null,
                    description: formData.description
                })
            });

            if (response.ok) {
                showToast(`Opération ${type} réussie`, "success");
                setFormData(prev => ({ ...prev, sku: '', quantity: 1, unit: '', description: '' }));
                setSelectedProduct(null);
                fetchData();
            } else {
                const error = await response.json();
                showToast(error.error || "Erreur lors de l'enregistrement", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur serveur", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-8">
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div>
                        <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Scanner & Saisie Rapide</h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1">Gérez vos entrées et sorties de stock en quelques clics</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Formulaire de saisie */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm">
                        <h2 className="text-lg font-bold flex items-center gap-2 mb-6">
                            <span className="material-symbols-outlined text-primary">edit_note</span>
                            Saisie de mouvement
                        </h2>

                        <div className="space-y-6">
                            {/* 1. Produit en premier */}
                            <div>
                                <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Référence (SKU)</label>
                                <div className="flex gap-2">
                                    <div className="flex-1 relative group">
                                        <ProductAutocomplete
                                            value={formData.sku}
                                            onChange={val => { setFormData({ ...formData, sku: val }); if (!val) setSelectedProduct(null); }}
                                            onSelect={product => {
                                                setSelectedProduct(product);
                                                const firstLoc = product.locations && product.locations.length > 0 ? product.locations[0] : null;
                                                setFormData({
                                                    ...formData,
                                                    sku: product.sku,
                                                    warehouseId: product.warehouseId ? String(product.warehouseId) : formData.warehouseId,
                                                    locationId: firstLoc ? String(firstLoc.id) : ''
                                                });
                                            }}
                                            companyId={user?.companyId}
                                            placeholder="Scannez ou cherchez (Nom, SKU, Réf)..."
                                        />
                                    </div>
                                    <button
                                        onClick={() => searchProduct(formData.sku)}
                                        disabled={loading || !formData.sku || !formData.warehouseId}
                                        className="h-14 px-6 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold active:scale-95 transition-all disabled:opacity-50"
                                    >
                                        Chercher
                                    </button>
                                </div>
                            </div>

                            {/* Blocks for creation flow (Mobile Alignment) */}
                            {offProduct && (
                                <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-2xl animate-in zoom-in-95 duration-300">
                                    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-blue-100 dark:border-blue-800/30">
                                        <div className="size-16 rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-blue-100 overflow-hidden shrink-0">
                                            <ProductImage src={offProduct.imageUrl} name={offProduct.name} />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 block">Trouvé sur {offProduct.source}</span>
                                            <h3 className="font-bold text-slate-900 dark:text-white leading-tight">{offProduct.name}</h3>
                                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">{offProduct.sku}</p>
                                        </div>
                                    </div>
                                    {offProduct.description && (
                                        <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 italic line-clamp-3">{offProduct.description}</p>
                                    )}
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => handleCreateProduct('Brouillon', offProduct)}
                                            className="px-4 py-3 bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-50 transition-colors"
                                        >
                                            Créer en Brouillon
                                        </button>
                                        <button
                                            onClick={() => handleCreateProduct('Actif', offProduct)}
                                            className="px-4 py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
                                        >
                                            Saisie Directe
                                        </button>
                                    </div>
                                </div>
                            )}

                            {unknownProduct && (
                                <div className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-2xl animate-in zoom-in-95 duration-300">
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="material-symbols-outlined text-amber-500">help_outline</span>
                                        <h3 className="font-bold text-amber-900 dark:text-amber-400">Produit inconnu - Création rapide</h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-amber-700 dark:text-amber-500 uppercase tracking-widest mb-1">Nom du produit *</label>
                                            <input
                                                type="text"
                                                className="w-full h-11 px-4 bg-white dark:bg-slate-800 border-2 border-amber-100 dark:border-amber-800/20 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 text-sm font-bold"
                                                placeholder="Saisissez le nom..."
                                                value={manualForm.name}
                                                onChange={e => setManualForm({ ...manualForm, name: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-amber-700 dark:text-amber-500 uppercase tracking-widest mb-1">Description (optionnel)</label>
                                            <textarea
                                                className="w-full p-4 bg-white dark:bg-slate-800 border-2 border-amber-100 dark:border-amber-800/20 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 text-sm font-bold resize-none h-20"
                                                placeholder="Détails du produit..."
                                                value={manualForm.description}
                                                onChange={e => setManualForm({ ...manualForm, description: e.target.value })}
                                            />
                                        </div>

                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                            />
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="w-full py-3 bg-white dark:bg-slate-800 border-2 border-dashed border-amber-200 dark:border-amber-800/30 rounded-xl flex items-center justify-center gap-2 text-amber-600 hover:bg-amber-100/50 transition-colors"
                                            >
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" className="h-10 w-10 object-cover rounded-lg" />
                                                ) : (
                                                    <span className="material-symbols-outlined text-xl">add_a_photo</span>
                                                )}
                                                <span className="text-xs font-bold uppercase tracking-widest">{imagePreview ? 'Changer la photo' : 'Ajouter une photo'}</span>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 pt-2">
                                            <button
                                                onClick={() => handleCreateProduct('Brouillon')}
                                                disabled={!manualForm.name || loading}
                                                className="px-4 py-3 bg-white dark:bg-slate-800 text-amber-700 dark:text-amber-500 border border-amber-200 dark:border-amber-800/50 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-100 transition-colors disabled:opacity-50"
                                            >
                                                Brouillon
                                            </button>
                                            <button
                                                onClick={() => handleCreateProduct('Actif')}
                                                disabled={!manualForm.name || loading}
                                                className="px-4 py-3 bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-amber-600/20 active:scale-95 transition-all disabled:opacity-50"
                                            >
                                                Saisie Directe
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 2. Entrepôt (auto-rempli si le produit en a un) */}
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Entrepôt</label>
                                <select
                                    required
                                    className={`w-full h-14 px-4 bg-slate-50 dark:bg-slate-800 border-2 rounded-xl outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white font-semibold transition-all ${!formData.warehouseId ? 'border-amber-500/50' : 'border-slate-200 dark:border-slate-700'}`}
                                    value={formData.warehouseId}
                                    onChange={e => setFormData({ ...formData, warehouseId: e.target.value, locationId: '' })}
                                >
                                    <option value="">-- Choisir un entrepôt --</option>
                                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>

                            {/* 3. Emplacement précis (filtré par emplacements du produit si disponible) */}
                            {formData.warehouseId && (() => {
                                const productLocIds = selectedProduct?.locations?.map(l => l.id) || [];
                                const filteredLocations = productLocIds.length > 0
                                    ? locations.filter(l => productLocIds.includes(l.id))
                                    : locations;
                                return filteredLocations.length > 0 && (
                                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">
                                            {productLocIds.length > 0 ? 'Emplacement du produit' : 'Emplacement Précis'}
                                        </label>
                                        <select
                                            className={`w-full h-14 px-4 bg-slate-50 dark:bg-slate-800 border-2 rounded-xl outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white font-semibold transition-all ${!formData.locationId ? 'border-amber-500/50' : 'border-slate-200 dark:border-slate-700'}`}
                                            value={formData.locationId}
                                            onChange={e => setFormData({ ...formData, locationId: e.target.value })}
                                        >
                                            <option value="">-- Choisir un emplacement --</option>
                                            {filteredLocations.map(loc => (
                                                <option key={loc.id} value={loc.id}>{loc.fullPath}</option>
                                            ))}
                                        </select>
                                        {productLocIds.length > 0 && (
                                            <p className="text-[10px] text-emerald-500 mt-2 italic px-1 font-bold">Filtré : {filteredLocations.length} emplacement(s) où ce produit est rangé.</p>
                                        )}
                                    </div>
                                );
                            })()}


                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Unité & Quantité</label>
                                    <div className="flex items-center gap-2">
                                        <select
                                            className={`h-14 px-4 pr-10 bg-slate-100 dark:bg-slate-800 border-2 rounded-xl focus:ring-2 focus:ring-primary outline-none font-bold text-sm text-slate-900 dark:text-white cursor-pointer ${!formData.unit ? 'border-amber-500/50' : 'border-transparent'}`}
                                            value={formData.unit}
                                            onChange={e => setFormData({ ...formData, unit: e.target.value })}
                                        >
                                            <option value="">--Choisir--</option>
                                            <option value="Unité">Unité</option>
                                            <option value="Carton">Carton</option>
                                            <option value="Palette">Palette</option>
                                        </select>


                                        <div className="flex flex-1 items-center gap-2">
                                            <button
                                                onClick={() => setFormData({ ...formData, quantity: Math.max(1, formData.quantity - 1) })}
                                                className="h-14 w-12 flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-xl font-bold"
                                            >
                                                -
                                            </button>
                                            <input
                                                className="flex-1 h-14 text-center bg-slate-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-primary outline-none font-black text-xl text-slate-900 dark:text-white w-20"
                                                type="number"
                                                value={formData.quantity}
                                                onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                            />
                                            <button
                                                onClick={() => setFormData({ ...formData, quantity: formData.quantity + 1 })}
                                                className="h-14 w-12 flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-xl font-bold"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col">
                                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Actions</label>
                                    <div className="flex gap-2 flex-1">
                                        <button
                                            onClick={() => handleMovement('Entrée')}
                                            disabled={loading}
                                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                                        >
                                            <span className="material-symbols-outlined font-bold">add_circle</span>
                                            <span className="text-[10px] uppercase">Entrée</span>
                                        </button>
                                        <button
                                            onClick={() => handleMovement('Sortie')}
                                            disabled={loading}
                                            className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 shadow-lg shadow-rose-500/20 disabled:opacity-50"
                                        >
                                            <span className="material-symbols-outlined font-bold">remove_circle</span>
                                            <span className="text-[10px] uppercase">Sortie</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                showConfirm({
                                                    title: 'Confirmer l\'ajustement',
                                                    message: `Voulez-vous vraiment fixer la quantité de cet article à ${formData.quantity} ? Cela écrasera le stock actuel.`,
                                                    type: 'warning',
                                                    onConfirm: () => handleMovement('Ajustement')
                                                });
                                            }}
                                            disabled={loading}
                                            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-black rounded-xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 shadow-lg shadow-amber-500/20 disabled:opacity-50"
                                        >
                                            <span className="material-symbols-outlined font-bold">tune</span>
                                            <span className="text-[10px] uppercase">Ajuster</span>
                                        </button>

                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Note / Description (optionnel)</label>
                                <textarea
                                    className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white font-medium resize-none h-24"
                                    placeholder="Ex: Réception commande #123..."
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Historique Récent & Aperçu */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm h-full overflow-hidden">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">history</span>
                                Dernières opérations
                            </h2>
                            <button onClick={() => navigate('/stocks')} className="text-xs font-black text-primary uppercase tracking-widest hover:underline">Voir tout</button>
                        </div>

                        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                            {recentMovements.length === 0 ? (
                                <div className="text-center py-12 text-slate-400 italic">
                                    Aucune opération récente
                                </div>
                            ) : recentMovements.map(m => (
                                <div key={m.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 group hover:border-primary/30 transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className={`size-12 rounded-full flex items-center justify-center font-bold ${m.type === 'Ajustement' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                                            (m.type.includes('Entrée') ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400')
                                            }`}>
                                            <span className="material-symbols-outlined">
                                                {m.type === 'Ajustement' ? 'tune' : (m.type.includes('Entrée') ? 'south_west' : 'north_east')}
                                            </span>
                                        </div>


                                        <div>
                                            <p className="font-black text-slate-900 dark:text-white text-sm">{m.product?.name}</p>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{m.product?.sku} • {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>

                                    </div>
                                    <span className={`text-sm font-black text-right ${m.type === 'Ajustement' ? 'text-amber-500' : (m.quantity > 0 ? 'text-emerald-600' : 'text-rose-600')}`}>
                                        {m.type === 'Ajustement' ? `Fixé à ${Math.abs(m.quantity)}` : (m.quantity > 0 ? `+${m.rawQuantity || m.quantity} ${m.unit || 'un.'}` : `${m.rawQuantity || m.quantity} ${m.unit || 'un.'}`)}
                                        {m.unit && m.unit !== 'Unité' && (
                                            <span className="block text-[10px] opacity-70">
                                                ({Math.abs(m.quantity)} unités)
                                            </span>
                                        )}
                                    </span>


                                </div>
                            ))}
                        </div>

                        {/* Visual Help Container */}
                        <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-2xl">
                            <h3 className="text-sm font-bold text-primary flex items-center gap-2 mb-2">
                                <span className="material-symbols-outlined text-sm">info</span>
                                Guide rapide
                            </h3>
                            <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-2 font-medium">
                                <li className="flex items-start gap-2">
                                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0"></span>
                                    Scannez le SKU ou tapez-le manuellement.
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0"></span>
                                    Vérifiez l'entrepôt de destination/départ.
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0"></span>
                                    Cliquez sur Entrée ou Sortie pour valider.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockScanner;
